import { LiHTMLAttributes } from 'react';

import { cn } from '@virginexperiencedays/tailwind';

import { BodyText } from '../../typography/BodyText';
import { Image } from '../../layout/Image';

import { roundPrice } from '../../utils/roundPrice';
import { useQuickBasketContext } from './utils/QuickBasketContext';
import { QuickBasketQuantityInput } from './QuickBasketQuantityInput';

import type { BasketItem } from '@virginexperiencedays/basket';
import { subtotalValues } from './utils/subtotalValues';

type Props = {
  items: BasketItem[];
};

const ListRenderer = ({ items = [] }: Props) => {
  const { isLoading, error, activePromoCode } = useQuickBasketContext();
  const { isPartialDiscount } = subtotalValues(items);

  if (isLoading || error) return null;

  return (
    <>
      {items.map(({ id, sku, title, qty, displayPrice, discountedPrice, image }) => {
        // if discount is applied only partially, highlight each discounted item
        const isPartiallyDiscounted =
          activePromoCode &&
          isPartialDiscount &&
          discountedPrice &&
          discountedPrice !== displayPrice;
        return (
          <BasketListItem key={id}>
            <div
              className={cn(
                'flex flex-1 gap-4',
                isPartiallyDiscounted && 'bg-background-neutral-faded -mx-4 px-4 py-2'
              )}
            >
              <Image
                className="image:object-cover image:object-center aspect-square h-16 w-16 flex-shrink-0 overflow-hidden rounded"
                src={image.src}
                alt={image?.alt ?? title}
                sizes="(max-width: 350px) 100px, 150px"
                fill
              />
              <div className="flex w-full items-stretch justify-between gap-2">
                <div className="line-clamp-3 self-stretch text-ellipsis">
                  <BodyText
                    size="small"
                    leading="normal"
                    className="text-neutral-strong font-semibold"
                  >
                    {title}
                  </BodyText>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <div className="flex flex-row flex-nowrap text-right">
                    <BodyText className="text-neutral-strong font-semibold">
                      {roundPrice(displayPrice)}
                    </BodyText>
                  </div>
                  <QuickBasketQuantityInput id={id} sku={sku} qty={qty} className="mt-auto" />
                </div>
              </div>
            </div>
          </BasketListItem>
        );
      })}
    </>
  );
};

export const QuickBasketList = (props: Props) => (
  <ul className="m-0 flex list-none flex-col flex-nowrap gap-2 p-0 pb-4">
    <ListRenderer {...props} />
  </ul>
);

const BasketListItem = ({ children, className, ...props }: LiHTMLAttributes<HTMLLIElement>) => (
  <li
    className={cn('border-b-background-neutral-faded mb-2 flex gap-4 border-b pb-4', className)}
    {...props}
  >
    {children}
  </li>
);
